import type { AudioCatalog } from "../catalog";
import { toMp3FallbackPath } from "../resource-paths";
import { LEVELS_BY_DOMAIN } from "@/content/levels-by-domain";
import { DOMAINS } from "@/domain";

import {
  classifyCurriculumVoiceCoverage,
  LETTER_SOUND_PRONUNCIATIONS
} from "./coverage";
import type {
  CurriculumAudioLocale,
  CurriculumVoiceSynthesis
} from "./types";

export { LETTER_SOUND_PRONUNCIATIONS };
export type {
  CurriculumAudioLocale,
  CurriculumVoiceSynthesis
} from "./types";

declare const curriculumVoiceTrackIdBrand: unique symbol;

export type CurriculumVoiceTrackId = string & {
  readonly [curriculumVoiceTrackIdBrand]: "CurriculumVoiceTrackId";
};

export interface CurriculumVoiceDefinition {
  readonly path: string;
  readonly mp3Path: string;
  readonly text: string;
  readonly locale: CurriculumAudioLocale;
  readonly levelIds: readonly string[];
  readonly synthesis: CurriculumVoiceSynthesis;
}

interface MutableVoiceDefinition {
  readonly path: string;
  readonly mp3Path: string;
  readonly text: string;
  readonly locale: CurriculumAudioLocale;
  readonly levelIds: Set<string>;
  readonly synthesis: CurriculumVoiceSynthesis;
}

function addDefinition(
  definitions: Map<string, MutableVoiceDefinition>,
  path: string,
  text: string,
  locale: CurriculumAudioLocale,
  levelId: string,
  synthesis: CurriculumVoiceSynthesis
): void {
  const normalizedText = text.trim();

  if (normalizedText.length === 0) {
    throw new Error("Curriculum audio caption is empty for " + path);
  }

  const existing = definitions.get(path);

  if (existing !== undefined) {
    if (
      existing.text !== normalizedText ||
      existing.locale !== locale ||
      existing.synthesis.kind !== synthesis.kind ||
      existing.synthesis.value !== synthesis.value
    ) {
      throw new Error("Conflicting curriculum audio definition for " + path);
    }

    existing.levelIds.add(levelId);
    return;
  }

  definitions.set(path, {
    path,
    mp3Path: toMp3FallbackPath(path),
    text: normalizedText,
    locale,
    levelIds: new Set([levelId]),
    synthesis
  });
}

export function collectCurriculumVoiceDefinitions(): CurriculumVoiceDefinition[] {
  const definitions = new Map<string, MutableVoiceDefinition>();

  for (const domain of DOMAINS) {
    for (const level of LEVELS_BY_DOMAIN[domain]) {
      const coverage = classifyCurriculumVoiceCoverage(level);

      for (const requirement of coverage.requirements) {
        addDefinition(
          definitions,
          requirement.path,
          requirement.text,
          requirement.locale,
          level.id,
          requirement.synthesis
        );
      }
    }
  }

  return [...definitions.values()]
    .sort((left, right) => left.path.localeCompare(right.path))
    .map((definition) => ({
      path: definition.path,
      mp3Path: definition.mp3Path,
      text: definition.text,
      locale: definition.locale,
      levelIds: [...definition.levelIds].sort(),
      synthesis: definition.synthesis
    }));
}

export const CURRICULUM_VOICE_DEFINITIONS =
  collectCurriculumVoiceDefinitions();

const curriculumVoiceTrackIds = new Set(
  CURRICULUM_VOICE_DEFINITIONS.map(({ path }) => path)
);

export function resolveCurriculumVoiceTrackId(
  path: string
): CurriculumVoiceTrackId | null {
  return curriculumVoiceTrackIds.has(path)
    ? (path as CurriculumVoiceTrackId)
    : null;
}

export const CURRICULUM_VOICE_TRACKS: AudioCatalog["voice"] =
  Object.fromEntries(
    CURRICULUM_VOICE_DEFINITIONS.map((definition) => [
      definition.path,
      {
        sources: [
          { src: definition.path, type: "audio/ogg" as const },
          { src: definition.mp3Path, type: "audio/mpeg" as const }
        ],
        gain: 1,
        captions: [
          {
            startMs: 0,
            endMs: 60_000,
            text: definition.text
          }
        ],
        fallbackCaption: definition.text
      }
    ])
  );
